import { invariant } from '@seen/core/invariant'
import { RecentSelections } from '@seen/core/models/recentSelections'
import Fuse from 'fuse.js'
import { compact, countBy, every, first } from 'lodash-es'
import {
  type JSX,
  memo,
  type ReactNode,
  type RefObject,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react'
import { OrderCommandLink } from '#src/components/app/command-menu/OrderCommandLink.tsx'
import { PatientCommandLink } from '#src/components/app/command-menu/PatientCommandLink.tsx'
import { SearchCommandLink } from '#src/components/app/command-menu/SearchCommandLink.tsx'
import type { ComboboxOption } from '#src/components/core/combobox/Combobox.tsx'
import {
  Command,
  CommandDialogContent,
  CommandInput,
  CommandItem,
  CommandList,
} from '#src/components/core/Command.tsx'
import { Dialog } from '#src/components/core/dialog/Dialog.tsx'
import { Hotkeys } from '#src/constants/hotkeys.ts'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useDebouncedCallback } from '#src/hooks/useDebouncedCallback.ts'
import { useDidChange } from '#src/hooks/useDidChange.ts'
import { useHotkeys } from '#src/hooks/useHotkeys.ts'
import { useOrders } from '#src/hooks/useOrders.ts'
import { useOrderSearchByIdentifier } from '#src/hooks/useOrderSearchByIdentifier.ts'
import { useParticipants } from '#src/hooks/useParticipants.ts'
import { usePortalStore } from '#src/hooks/usePortalStore.ts'
import { useRead } from '#src/hooks/useRead.ts'

export function CommandMenu(): JSX.Element {
  const open = usePortalStore((state) => state.commandMenuOpen)
  const setOpen = usePortalStore((state) => state.setCommandMenuOpen)

  useHotkeys(Hotkeys.CommandMenu, () => setOpen((open) => !open), {
    enableOnFormTags: ['input', 'textarea', 'select'],
    enableOnContentEditable: true,
    preventDefault: true,
  })

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <CommandDialogContent className="shadow-none">
        <CommandMenuContent />
      </CommandDialogContent>
    </Dialog>
  )
}

function CommandMenuContent(): JSX.Element {
  const commandListRef = useRef<HTMLDivElement>(null)

  const [search, setSearch] = useState('')

  const participants = useParticipants()
  const participantOptions = useMemo(
    () =>
      participants.map((p) => ({
        value: p.reference.reference,
        label: (
          <PatientCommandLink
            key={p.reference.reference}
            data={{
              type: 'participant',
              value: p.reference.reference,
              participant: p,
            }}
            highlightedInitials={
              search.length === 2 && isInitialMatch(search, p.name)
                ? search
                : undefined
            }
          />
        ),
        name: p.name,
        nameFirstLast: `${p.firstName} ${p.lastName}`,
        nameLastFirst: `${p.lastName} ${p.firstName}`,
        keywords: p.searchKeywords,
      })),
    [participants, search]
  )

  const { data: orders } = useOrders()
  const orderOptions = useMemo(
    () =>
      (orders ?? []).map((o) => ({
        value: o.reference.reference,
        label: (
          <OrderCommandLink
            data={{ type: 'order', value: o.reference.reference, order: o }}
          />
        ),
        keywords: o.searchKeywordsIdentifiers,
      })),
    [orders]
  )

  const orderOptionByKeyword = useMemo(
    () =>
      orderOptions.reduce<Record<string, ComboboxOption<string>>>((acc, o) => {
        o.keywords.forEach((k) => {
          acc[normalizeOrderKeyword(k)] = o
        })
        return acc
      }, {}),
    [orderOptions]
  )

  // The default order list excludes completed/revoked orders for performance,
  // so searching a completed order's SR identifier wouldn't surface it. Look it
  // up on demand; the hook no-ops for non-identifier input.
  const { data: identifierOrders } = useOrderSearchByIdentifier(search)
  const identifierOrderOption = useMemo<
    ComboboxOption<string> | undefined
  >(() => {
    const order = first(identifierOrders)
    if (!order) {
      return undefined
    }
    return {
      value: order.reference.reference,
      label: (
        <OrderCommandLink
          data={{
            type: 'order',
            value: order.reference.reference,
            order,
          }}
        />
      ),
      keywords: order.searchKeywordsIdentifiers,
    }
  }, [identifierOrders])

  const currentPractitioner = useCurrentPractitioner()
  const { data: recentSelections } = useRead(
    RecentSelections,
    currentPractitioner?.recentSelections
  )
  const indexByValue = useMemo(
    () => new Map((recentSelections?.values ?? []).map((o, idx) => [o, idx])),
    [recentSelections]
  )

  const recentParticipants = useMemo(
    () =>
      participantOptions.sort((a, b) => {
        invariant(a.value, 'Value is required')
        invariant(b.value, 'Value is required')

        const aIndex = indexByValue.get(a.value)
        const bIndex = indexByValue.get(b.value)

        if (aIndex === undefined && bIndex === undefined) {
          return 0
        }
        if (aIndex === undefined) {
          return 1
        }
        if (bIndex === undefined) {
          return -1
        }

        return aIndex - bIndex
      }),
    [participantOptions, indexByValue]
  )

  const recentSelectionsLength = useMemo(
    () => recentSelections?.values?.length ?? 0,
    [recentSelections]
  )

  const fuse = useMemo(
    () =>
      new Fuse(recentParticipants, {
        keys: [
          {
            name: 'keywords',
            weight: 0.2,
          },
          {
            name: 'name',
          },
          {
            name: 'nameFirstLast',
          },
          {
            name: 'nameLastFirst',
          },
        ],
        includeScore: true,
        sortFn: (a, b) => {
          const aValue = recentParticipants[a.idx].value
          const bValue = recentParticipants[b.idx].value
          const aIndex = indexByValue.get(aValue)
          const bIndex = indexByValue.get(bValue)
          const aSortValue = getSortValue(
            a.score,
            aIndex,
            recentSelectionsLength
          )
          const bSortValue = getSortValue(
            b.score,
            bIndex,
            recentSelectionsLength
          )
          return aSortValue - bSortValue
        },
      }),
    [recentParticipants, recentSelectionsLength, indexByValue]
  )

  const options = useMemo<ComboboxOption<string>[]>(() => {
    if (!search) {
      return recentParticipants.slice(0, MAX_OPTIONS_TO_SHOW)
    }
    const orderResult =
      orderOptionByKeyword[normalizeOrderKeyword(search)] ??
      identifierOrderOption
    const participantResults = fuse.search(search, {
      limit: orderResult ? MAX_OPTIONS_TO_SHOW - 1 : MAX_OPTIONS_TO_SHOW,
    })

    if (search.length === 2) {
      const initialMatches = recentParticipants.filter((p) =>
        isInitialMatch(search, p.name)
      )
      return [
        ...initialMatches,
        ...participantResults
          .filter((r) => !initialMatches.includes(r.item))
          .map((r) => r.item),
      ].slice(0, MAX_OPTIONS_TO_SHOW)
    }

    return compact([orderResult, ...participantResults.map((r) => r.item)])
  }, [
    recentParticipants,
    search,
    fuse,
    orderOptionByKeyword,
    identifierOrderOption,
  ])

  const firstValue = useMemo(() => {
    const firstOption = first(options)
    return firstOption?.value ?? ''
  }, [options])

  const commandItems = useMemo(
    () => options.map((option) => option.label),
    [options]
  )

  const [highlightedValue, setHighlightedValue] = useState(firstValue)

  const highlightFirstItem = useCallback(() => {
    setHighlightedValue(firstValue)
    commandListRef.current?.scrollTo({ top: 0, behavior: 'instant' })
  }, [firstValue])

  // Highlight the first item if it changes (i.e. new data is loaded)
  const didFirstItemChange = useDidChange(firstValue)
  if (didFirstItemChange && highlightedValue !== firstValue) {
    highlightFirstItem()
  }

  const handleInputChange = useDebouncedCallback((value: string) => {
    setSearch(value)
    highlightFirstItem()
  }, 50)

  return (
    <CommandMenuCommand
      search={search}
      value={highlightedValue}
      onValueChange={setHighlightedValue}
      onInputChange={handleInputChange}
      items={commandItems}
      commandListRef={commandListRef}
    />
  )
}

const CommandMenuCommand = memo(function CommandMenuCommand({
  search,
  value,
  onValueChange,
  onInputChange,
  items,
  commandListRef,
}: {
  search: string
  value: string
  onValueChange: (value: string) => void
  onInputChange: (input: string) => void
  items: ReactNode[]
  commandListRef: RefObject<HTMLDivElement | null>
}) {
  const firstItem = useMemo(() => items[0], [items])
  const restItems = useMemo(() => items.slice(1), [items])
  const isAdmin = usePortalStore((state) => state.showAdminActions)
  return (
    <Command value={value} onValueChange={onValueChange} shouldFilter={false}>
      <CommandInput
        autoFocus
        onValueChange={onInputChange}
        placeholder="Search…"
        className="rounded-none p-5 text-lg leading-none"
      />
      <CommandList viewportRef={commandListRef}>
        {firstItem}
        {search && isAdmin && <SearchCommandLink search={search} />}
        {restItems}
        {items.length >= MAX_OPTIONS_TO_SHOW && (
          <CommandItem
            disabled
            className="flex h-[3.365rem] items-center justify-center"
          >
            Search to see more results…
          </CommandItem>
        )}
      </CommandList>
    </Command>
  )
})

const MAX_OPTIONS_TO_SHOW = 50 // Search performance is better with fewer options and it's hard to imagine people scrolling through more than 50 options.

function getSortValue(
  score: number,
  index: number | undefined,
  length: number
): number {
  if (index === undefined) {
    return score
  }

  /**
   * We calculate a multiplier so that items that are more recently selected are ranked higher. Note that the closer the score is to 0, the higher it's ranked.
   *
   * Here are some sample multipliers if length is 10:
   * Index 0:
   *   indexFactor: (10 - 0) / 10) = 1
   *   multiplier: (1 + 1) * 10 = 20
   * Index 1:
   *   indexFactor: (10 - 1) / 10) = 0.9
   *   multiplier: (1 + 0.9) * 10 = 19
   * Index 8:
   *   indexFactor: (10 - 8) / 10) = 0.2
   *   multiplier: (1 + 0.2) * 10 = 12
   * Index 9:
   *   indexFactor: (10 - 9) / 10) = 0.1
   *   multiplier: (1 + 0.1) * 10 = 11
   */
  const indexFactor = (length - index) / length
  const multiplier = (1 + indexFactor) * 10 // 10 - 20

  return score / multiplier
}

function isInitialMatch(search: string, name: string): boolean {
  const searchInitials = search.toLowerCase().split('')
  const nameInitials = tokens(name).map((t) => t[0])
  return isMultisetSubset(searchInitials, nameInitials)
}

function tokens(s: string): string[] {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/gi, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
}

function isMultisetSubset<T>(subset: T[], superset: T[]): boolean {
  const subsetCounts = countBy(subset)
  const supersetCounts = countBy(superset)

  return every(
    subsetCounts,
    (count, value) => (supersetCounts[value] ?? 0) >= count
  )
}

function normalizeOrderKeyword(keyword: string): string {
  return keyword.trim().toLowerCase().replace('-', '')
}
